import type { NextRequest } from 'next/server'

type SiteIdInput = string | number | null | undefined

const normalizeSiteId = (value: SiteIdInput): string | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(Math.trunc(value)) : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  return null
}

const resolvedDefaultSiteId = normalizeSiteId(
  process.env.NEXT_PUBLIC_DEFAULT_SITE_ID
)

if (!resolvedDefaultSiteId) {
  throw new Error(
    'NEXT_PUBLIC_DEFAULT_SITE_ID must be defined for site-aware API routing.'
  )
}

// BACKEND_URL is a server-only runtime env var pointing directly to the backend
// (e.g. http://localhost:16001). It avoids the loop where NEXT_PUBLIC_API_BASE_URL
// points back to the primal frontend through the reverse proxy.
export const API_BASE_URL =
  process.env.BACKEND_URL || process.env.NEXT_PUBLIC_API_BASE_URL

export const DEFAULT_SITE_ID = resolvedDefaultSiteId

export const resolveSiteId = (request: NextRequest): string => {
  const querySiteId = request.nextUrl.searchParams.get('siteId')
  return ensureSiteId(querySiteId)
}

export const ensureSiteId = (value: SiteIdInput): string =>
  normalizeSiteId(value) || DEFAULT_SITE_ID

// Domain resolution cache (bounded to prevent memory leaks)
const DOMAIN_CACHE = new Map<string, { siteId: string; timestamp: number }>()
const DOMAIN_CACHE_TTL = 300_000 // 5 minutes
const DOMAIN_CACHE_MAX = 1000

/**
 * Resolve siteId from a hostname via the backend API, with caching.
 * Returns null when the domain is unknown.
 */
export async function resolveSiteIdFromDomain(
  hostname: string
): Promise<string | null> {
  const normalized = hostname.trim().toLowerCase().split(':')[0]
  if (!normalized || normalized === 'localhost') return null

  const cached = DOMAIN_CACHE.get(normalized)
  if (cached && Date.now() - cached.timestamp < DOMAIN_CACHE_TTL) {
    return cached.siteId
  }

  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(
      `${API_BASE_URL}/api/sites/resolve?domain=${encodeURIComponent(normalized)}`,
      { signal: controller.signal, headers: { Accept: 'application/json' } }
    )

    clearTimeout(timeout)

    if (response.ok) {
      const data = await response.json()
      if (data.success && data.data?.siteId) {
        const siteId = String(data.data.siteId)
        if (DOMAIN_CACHE.size >= DOMAIN_CACHE_MAX) {
          let oldestKey: string | null = null
          let oldestTime = Infinity
          for (const [key, entry] of DOMAIN_CACHE.entries()) {
            if (entry.timestamp < oldestTime) {
              oldestTime = entry.timestamp
              oldestKey = key
            }
          }
          if (oldestKey) DOMAIN_CACHE.delete(oldestKey)
        }
        DOMAIN_CACHE.set(normalized, { siteId, timestamp: Date.now() })
        return siteId
      }
    }
  } catch (error) {
    console.error(`[base] Domain resolution failed for ${normalized}:`, error)
  }

  return null
}

/**
 * Resolve current site id for server-side API routes.
 * Tries domain resolution from request hostname first, falls back to DEFAULT_SITE_ID.
 */
export async function getSiteId(request?: NextRequest): Promise<string> {
  if (request) {
    const hostname =
      request.headers.get('x-forwarded-host') || request.headers.get('host')
    if (hostname) {
      const resolved = await resolveSiteIdFromDomain(hostname)
      if (resolved) return resolved
    }
  }
  return DEFAULT_SITE_ID
}

const ensureLeadingSlash = (value: string): string =>
  value.startsWith('/') ? value : `/${value}`

const buildProxyPath = (path: string): string => {
  const normalized = ensureLeadingSlash(path)
  return `/api/be${normalized}`
}

export const buildProxyUrl = (request: NextRequest, path: string): string => {
  return new URL(buildProxyPath(path), request.url).toString()
}

type ProxyFetchOptions = RequestInit & {
  siteId?: SiteIdInput
}

export const proxyFetch = (
  request: NextRequest,
  path: string,
  options: ProxyFetchOptions = {}
) => {
  const { siteId, headers: initHeaders, ...rest } = options
  const headers = new Headers(initHeaders || {})

  const resolvedSiteId = siteId ? ensureSiteId(siteId) : null
  const url = new URL(buildProxyUrl(request, path))
  if (resolvedSiteId) {
    url.searchParams.set('siteId', resolvedSiteId)
  }

  return fetch(url.toString(), {
    ...rest,
    headers,
  })
}
